// src/pages/outsourcing_management/requisitions/partEligibility.ts
//
// Which parts a requisition of a given type may be raised against, and how a
// part's finish reads on screen.
import {
  ManufacturedProductDetailsType,
  ProductTypeEnum,
} from "types/products/ProductCommandsType";
import { REQ_TYPE_CNC, REQ_TYPE_SHEET } from "./requisitionForm";

// A requisition asks a supplier for one kind of work, so the part list should
// only offer parts that kind of work applies to - an order's sheet part has no
// business appearing under a CNC requisition.
const PART_TYPES_BY_REQUISITION_TYPE: Record<string, ProductTypeEnum[]> = {
  [REQ_TYPE_CNC]: [ProductTypeEnum.CNC],
  [REQ_TYPE_SHEET]: [ProductTypeEnum.SHEET],
  // REQ_TYPE_FINISH is deliberately absent. Finishing is applied *to* a part
  // rather than being a kind of part, so the part's type does not decide
  // eligibility - it is whether the part records a finish, which the
  // requireFinish rule in the pickers enforces on top of this.
};

// The only parts a supplier can be asked to work on: the ones we make. OTHER
// is a bought-in line on the order, CUSTOM a hand-priced one, FEE a charge and
// BASE a template - none has a model or drawing to send, so none is offered
// under any requisition type, Custom included.
export const MANUFACTURED_PART_TYPES: ProductTypeEnum[] = [
  ProductTypeEnum.CNC,
  ProductTypeEnum.SHEET,
  ProductTypeEnum.PRINT3D,
];

const STANDARD_FINISH = "standard";

/** The part types a requisition of this type may be raised against. */
export const allowedPartTypes = (
  typeCode: string | undefined
): ProductTypeEnum[] =>
  (typeCode && PART_TYPES_BY_REQUISITION_TYPE[typeCode]) ||
  MANUFACTURED_PART_TYPES;

export const isPartEligible = (
  productType: ProductTypeEnum | undefined,
  typeCode: string | undefined
): boolean => !!productType && allowedPartTypes(typeCode).includes(productType);

// A finishing requisition is *about* a finish, so the part has to name one -
// a supplier asked to finish a part with no finish recorded has been sent a
// question with the answer missing. The backend refuses it; showing it here
// means the requester sees which parts are eligible while choosing, rather
// than after saving.
export const finishLabel = (
  details: ManufacturedProductDetailsType | undefined
): string | undefined => {
  const custom = details?.customFinish?.name;
  if (custom) return custom;

  // `finish` is typed string[] but the column is a String(255) and the VM
  // passes it through verbatim, so what actually arrives is a comma-separated
  // string ("standard,electro-polish-default"). Both shapes are handled: the
  // type is wrong rather than the data, and correcting the type is a wider
  // change than this needs.
  const raw: unknown = details?.finish;
  const parts = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
    ? raw.split(",")
    : [];
  // "standard" is the absence of a finish rather than a finish: it is what
  // the configurator records when the customer asked for nothing in
  // particular, so a part carrying only that has nothing to send a finishing
  // supplier. It is dropped from a combination too - naming it alongside a
  // real finish only pads the label.
  const named = parts
    .map((f) => String(f).trim())
    .filter((f) => f && f.toLowerCase() !== STANDARD_FINISH);
  return named.length ? named.join(", ") : undefined;
};
